/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback } from "react";
import { logger } from "@/utils/logger";
import { motion, AnimatePresence } from "framer-motion";
import { Home, Volume2, VolumeX, RotateCcw, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import Confetti from "react-confetti";
import { useWindowSize } from "@/hooks/useWindowSize";
import { recordGameWon } from "@/utils/playerProfile";
import { ANIMALS as ALL_ANIMALS } from "@/utils/quizData";

import { getVoiceLang } from "@/utils/voicePref";
// Only animals with audible sounds for "tebak suara"
const ANIMALS = ALL_ANIMALS.filter(a => a.sound && a.sound !== "Diam");

const speakText = (t) => {
  try {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(t);
    u.lang = getVoiceLang(); u.rate = 0.85; u.pitch = 1.0;
    window.speechSynthesis.speak(u);
  } catch (err) { logger.debug("speakText failed", err); }
};

const TebakSuara = ({ onBackToMenu }) => {
  const { width: winW, height: winH } = useWindowSize();
  const [score, setScore] = useState(0);
  const [round, setRound] = useState(0);
  const [target, setTarget] = useState(null);
  const [options, setOptions] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [soundOn, setSoundOn] = useState(true);
  const [won, setWon] = useState(false);
  const totalRounds = 12;

  const nextQuestion = useCallback(() => {
    const correct = ANIMALS[Math.floor(Math.random() * ANIMALS.length)];
    const wrongs = [];
    while (wrongs.length < 3) {
      const w = ANIMALS[Math.floor(Math.random() * ANIMALS.length)];
      if (w.name !== correct.name && !wrongs.find(x => x.name === w.name)) wrongs.push(w);
    }
    const opts = [...wrongs, correct].sort(() => Math.random() - 0.5);
    setTarget(correct); setOptions(opts); setFeedback(null);
    setTimeout(() => { if (soundOn) speakText(`Which animal makes the sound ${correct.sound}?`); }, 400);
  }, [soundOn]);

  useEffect(() => { nextQuestion(); /* eslint-disable-next-line */ }, []);

  const handlePick = (a) => {
    if (feedback) return;
    if (a.name === target.name) {
      setFeedback({ type: "ok", text: `Benar! ${a.name}!` });
      setScore(s => s + 1);
      if (soundOn) speakText(`Correct! That's ${a.name}!`);
    } else {
      setFeedback({ type: "wrong", text: `Itu ${a.name}, jawabannya ${target.name}` });
      if (soundOn) speakText(`Not, that's ${a.name}. The correct answer is ${target.name}`);
    }
    setTimeout(() => {
      if (round + 1 >= totalRounds) {
        setWon(true);
        recordGameWon("suara");
        if (soundOn) speakText(`You got ${score + (a.name === target.name ? 1 : 0)} of ${totalRounds}`);
      } else {
        setRound(r => r + 1);
        nextQuestion();
      }
    }, 1800);
  };

  const repeat = () => { if (target && soundOn) speakText(`${target.sound}`); };

  if (won) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] px-4" data-testid="tebak-suara-win">
        <Confetti width={winW} height={winH} recycle={false} numberOfPieces={250} />
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="glass-card-strong rounded-2xl p-8 text-center max-w-md w-full">
          <span className="text-6xl block mb-3">🏆</span>
          <h2 className="font-fredoka text-3xl text-gold font-bold mb-2">Selesai!</h2>
          <p className="font-fredoka text-gold-light/80 mb-4">Skor: {score}/{totalRounds}</p>
          <div className="flex flex-col gap-2">
            <Button onClick={() => { setScore(0); setRound(0); setWon(false); nextQuestion(); }}
              className="w-full font-fredoka font-bold rounded-xl h-10 bg-gradient-to-r from-yellow-400 to-amber-500"><RotateCcw className="w-4 h-4 mr-2" />Main Lagi</Button>
            <Button onClick={onBackToMenu} variant="outline" className="w-full glass-card border-gold/20 text-gold font-fredoka rounded-xl">Menu</Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center w-full max-w-4xl mx-auto px-3 pt-3" data-testid="tebak-suara-game">
      <div className="w-full glass-card-strong rounded-xl p-2 mb-2 flex justify-between items-center">
        <span className="font-fredoka text-gold font-bold text-sm">🎵 {round + 1}/{totalRounds} • Skor: {score}</span>
        <div className="flex gap-1.5">
          <Button onClick={() => setSoundOn(!soundOn)} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 w-7 p-0">{soundOn ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}</Button>
          <Button onClick={onBackToMenu} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 px-2"><Home className="w-3 h-3" /></Button>
        </div>
      </div>

      <motion.div key={round} initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="glass-card-strong rounded-2xl p-6 w-full text-center mb-3">
        <p className="font-fredoka text-gold-light/70 text-sm mb-1">Suara hewan apa ini?</p>
        <p className="font-fredoka text-4xl sm:text-5xl text-gold font-bold mb-3">"{target?.sound}"</p>
        <Button data-testid="tebak-suara-replay" onClick={repeat} className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-fredoka font-bold rounded-xl h-10 px-4">
          <Play className="w-4 h-4 mr-2" />Dengar Lagi
        </Button>
      </motion.div>

      <AnimatePresence>
        {feedback && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ opacity: 0 }} className="mb-3" data-testid={`tebak-suara-feedback-${feedback.type}`}>
            <span className={`font-fredoka font-bold text-base px-4 py-2 rounded-full ${feedback.type === "ok" ? "bg-green-500 text-white" : "bg-red-400 text-white"}`}>
              {feedback.type === "ok" ? "✅" : "❌"} {feedback.text}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-2 gap-4 w-full">
        {options.map((a, i) => (
          <motion.button key={`${a.name}-${i}`} data-testid={`tebak-suara-opt-${i}`}
            onClick={() => handlePick(a)} disabled={!!feedback}
            whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.95 }}
            className="ingredient-3d quiz-option-btn flex flex-col items-center text-slate-800 font-fredoka font-bold">
            <span className="quiz-option-emoji mb-1">{a.emoji}</span>
            <span style={{ fontSize: "clamp(0.875rem, 2vw, 1.25rem)" }}>{a.name}</span>
          </motion.button>
        ))}
      </div>
    </div>
  );
};

export default TebakSuara;
